import React from 'react';

type SimulationsPageProps = {
  onBack?: () => void;
  onStart?: (opts: { instructions: string; voice?: string; topic?: string }) => void;
};

const SimulationsPage: React.FC<SimulationsPageProps> = ({ onBack, onStart }) => {
  // Customer Support configuration
  const STORAGE_KEY = 'sim_cfg_customer_support';
  const getDefaultCfg = () => ({ problem_severity: 5, emotional_intensity: 5, customer_cooperativeness: 5 });
  const loadCfg = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return getDefaultCfg();
      const parsed = JSON.parse(raw || '{}');
      const clamp = (v: number) => Math.min(10, Math.max(1, Number(v) || 5));
      return {
        problem_severity: clamp(parsed.problem_severity),
        emotional_intensity: clamp(parsed.emotional_intensity),
        customer_cooperativeness: clamp(parsed.customer_cooperativeness)
      };
    } catch {
      return getDefaultCfg();
    }
  };
  const [csCfg, setCsCfg] = React.useState(loadCfg());
  const [editingCS, setEditingCS] = React.useState(false);

  const saveCfg = (cfg: typeof csCfg) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg));
    setCsCfg(cfg);
  };

  const buildCustomerSupportInstructions = (cfg: typeof csCfg) => {
    const template = `You are the CUSTOMER and you will remain the customer for the entire call.\nThe USER is the support representative.\n\nYour output MUST be a first‑person customer utterance only (symptoms, feelings, short clarifications, answers to the USER's questions provided the questions are related to the problem you're describing).\nDo NOT offer troubleshooting steps, instructions, or solutions.\nDo NOT ask diagnostic questions (e.g., “can you check…”, “please try…”, “tell me what…”).\nDo NOT switch roles, even if the user is brief or unclear.\nKeep responses short: 1–2 sentences per turn, natural and conversational.\n\nSettings (for your behavior only):\n- Problem Severity: {{problem_severity}}\n- Emotional Intensity: {{emotional_intensity}}\n- Cooperativeness: {{customer_cooperativeness}}\n\nAllowed intents for your next utterance:\n- complaint (state/describe the problem),\n- providing context (provide additional information if requested to elaborate or answer a direct question if you feel its related to the issue),\n- emotion (express how you feel),\n- brief elaboration (add a small detail).\nIf your next response would be anything else, regenerate as one of the allowed intents.\n\nStyle guidelines:\n- Natural rhythm with contractions and mild fillers is okay; do not be verbose.\n- Match the sliders: higher severity ⇒ more impact; higher intensity ⇒ more tension; lower cooperativeness ⇒ fewer details.\n- React realistically to the rep’s last turn (ease tension if helpful, push back if not). Make up answers to questions if you feel the questions are related to the issue, ask for clarification if not. If asked for your name or username, its Ron Sawyer, rsawyer-48.\n- End only when the issue feels resolved relative to severity.\n\nFirst turn: open with a 1–2 sentence description of your issue at the given severity and intensity, then pause.`;
    return template
      .replace('{{problem_severity}}', String(cfg.problem_severity))
      .replace('{{emotional_intensity}}', String(cfg.emotional_intensity))
      .replace('{{customer_cooperativeness}}', String(cfg.customer_cooperativeness));
  };

  return (
    <div className="bg-white rounded-lg shadow h-full overflow-auto">
      {/* Header block copied in style from Skills Manager (visual only) */}
      <div className="border-b border-gray-200 py-6 px-6 bg-white">
        <h1 className="text-2xl font-semibold text-gray-800 text-center">Simulate real-world conversations</h1>
        {onBack && (
          <div className="mt-3 text-center">
            <button onClick={onBack} className="text-sm text-gray-600 hover:text-gray-800">Back</button>
          </div>
        )}
      </div>

      {/* Five cards styled like category cards, stacked vertically with left image, middle text, right actions */}
      <div className="p-6">
        <div className="grid grid-cols-1 gap-4">
          {/* 1) Customer Support Call */}
          <div className="bg-white rounded-lg shadow-sm border p-4 hover:shadow transition-shadow flex flex-col relative">
            {editingCS ? (
              <div className="flex-1 flex flex-col">
                <div className="text-base font-semibold text-gray-900 mb-3">Customer Support Call</div>
                <div className="grid grid-cols-1 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Problem Severity: <span className="font-semibold">{csCfg.problem_severity}</span></label>
                    <input type="range" min={1} max={10} value={csCfg.problem_severity} onChange={(e)=> setCsCfg(v=> ({...v, problem_severity: Number(e.target.value)}))} className="w-full" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Emotional Intensity: <span className="font-semibold">{csCfg.emotional_intensity}</span></label>
                    <input type="range" min={1} max={10} value={csCfg.emotional_intensity} onChange={(e)=> setCsCfg(v=> ({...v, emotional_intensity: Number(e.target.value)}))} className="w-full" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Customer Cooperativeness: <span className="font-semibold">{csCfg.customer_cooperativeness}</span></label>
                    <input type="range" min={1} max={10} value={csCfg.customer_cooperativeness} onChange={(e)=> setCsCfg(v=> ({...v, customer_cooperativeness: Number(e.target.value)}))} className="w-full" />
                  </div>
                </div>
                <div className="mt-auto flex items-center justify-end gap-3 pt-3">
                  <button className="text-sm text-gray-600 hover:text-gray-800" onClick={()=> { setCsCfg(loadCfg()); setEditingCS(false); }}>Cancel</button>
                  <button className="bg-[#25394D] hover:brightness-110 text-white py-2 px-3 rounded-md text-sm" onClick={()=> { saveCfg(csCfg); setEditingCS(false); }}>Save</button>
                </div>
              </div>
            ) : (
              <div className="flex flex-row items-stretch gap-4">
                <button
                  aria-label="Configure"
                  title="Configure"
                  className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
                  onClick={()=> setEditingCS(true)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                    <path fillRule="evenodd" d="M11.078 2.25c-.917 0-1.699.663-1.85 1.57l-.143.859a8.25 8.25 0 0 0-1.962 1.133l-.809-.334a1.875 1.875 0 0 0-2.401 1.09l-.75 1.846a1.875 1.875 0 0 0 1.09 2.401l.809.334a8.284 8.284 0 0 0 0 2.266l-.81.334a1.875 1.875 0 0 0-1.089 2.4l.75 1.847a1.875 1.875 0 0 0 2.4 1.089l.81-.334a8.25 8.25 0 0 0 1.961 1.133l.144.86c.15.906.932 1.569 1.85 1.569h1.5c.918 0 1.7-.663 1.85-1.57l.144-.859a8.25 8.25 0 0 0 1.961-1.133l.81.334a1.875 1.875 0 0 0 2.4-1.09l.75-1.846a1.875 1.875 0 0 0-1.089-2.401l-.81-.334a8.284 8.284 0 0 0 0-2.266l.81-.334a1.875 1.875 0 0 0 1.089-2.4l-.75-1.847a1.875 1.875 0 0 0-2.4-1.089l-.81.334a8.25 8.25 0 0 0-1.961-1.133l-.144-.86a1.875 1.875 0 0 0-1.85-1.569h-1.5Zm.75 8.25a2.25 2.25 0 1 0 0 4.5 2.25 2.25 0 0 0 0-4.5Z" clipRule="evenodd" />
                  </svg>
                </button>
                <div className="w-40 h-32 rounded overflow-hidden bg-gray-100 flex-shrink-0">
                  <img
                    src="/simulations/customer_support_center.jpeg"
                    alt="Customer Support Call"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const el = e.currentTarget as HTMLImageElement;
                      el.style.display = 'none';
                    }}
                  />
                </div>
                <div className="flex-1 flex flex-col py-1 gap-1">
                  <div className="text-base font-semibold text-gray-900">Customer Support Call</div>
                  <div className="text-sm text-gray-600">
                    You're a new tier one agent in a call center for a leading brand. You're next on deck and your customer is waiting...
                  </div>
                </div>
                <div className="flex flex-col items-end justify-end gap-2">
                  <button
                    className="bg-[#25394D] hover:brightness-110 text-white py-2 px-3 rounded-md text-sm"
                    onClick={() => onStart && onStart({
                      instructions: buildCustomerSupportInstructions(csCfg),
                      voice: 'cedar',
                      topic: 'Customer Support Call'
                    })}
                  >
                    Lets do it!
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* 2) Sales Development Call */}
          <div className="bg-white rounded-lg shadow-sm border p-4 hover:shadow transition-shadow flex flex-row items-stretch gap-4 relative">
            <button
              aria-label="Configure"
              title="Configure"
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
              onClick={()=> {}}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path fillRule="evenodd" d="M11.078 2.25c-.917 0-1.699.663-1.85 1.57l-.143.859a8.25 8.25 0 0 0-1.962 1.133l-.809-.334a1.875 1.875 0 0 0-2.401 1.09l-.75 1.846a1.875 1.875 0 0 0 1.09 2.401l.809.334a8.284 8.284 0 0 0 0 2.266l-.81.334a1.875 1.875 0 0 0-1.089 2.4l.75 1.847a1.875 1.875 0 0 0 2.4 1.089l.81-.334a8.25 8.25 0 0 0 1.961 1.133l.144.86c.15.906.932 1.569 1.85 1.569h1.5c.918 0 1.7-.663 1.85-1.57l.144-.859a8.25 8.25 0 0 0 1.961-1.133l.81.334a1.875 1.875 0 0 0 2.4-1.09l.75-1.846a1.875 1.875 0 0 0-1.089-2.401l-.81-.334a8.284 8.284 0 0 0 0-2.266l.81-.334a1.875 1.875 0 0 0 1.089-2.4l-.75-1.847a1.875 1.875 0 0 0-2.4-1.089l-.81.334a8.25 8.25 0 0 0-1.961-1.133l-.144-.86a1.875 1.875 0 0 0-1.85-1.569h-1.5Zm.75 8.25a2.25 2.25 0 1 0 0 4.5 2.25 2.25 0 0 0 0-4.5Z" clipRule="evenodd" />
              </svg>
            </button>
            <div className="w-40 h-32 rounded overflow-hidden bg-gray-100 flex-shrink-0">
              <img
                src="/simulations/sales_development_rep.jpeg"
                alt="Sales Development Call"
                className="w-full h-full object-cover"
                onError={(e) => {
                  const el = e.currentTarget as HTMLImageElement;
                  el.style.display = 'none';
                }}
              />
            </div>
            <div className="flex-1 flex flex-col py-1 gap-1">
              <div className="text-base font-semibold text-gray-900">Sales Development Call</div>
              <div className="text-sm text-gray-600">
                You're a top performing sales development rep for a leading b2b brand. Just one more call to reclaim the leaderboard this month. Your lead just picked up the phone, you know what to do...
              </div>
            </div>
            <div className="flex flex-col items-end justify-end gap-2">
              <button className="bg-[#25394D] hover:brightness-110 text-white py-2 px-3 rounded-md text-sm">Lets do it!</button>
            </div>
          </div>

          {/* 3) Leading an Effective Meeting */}
          <div className="bg-white rounded-lg shadow-sm border p-4 hover:shadow transition-shadow flex flex-row items-stretch gap-4 relative">
            <button
              aria-label="Configure"
              title="Configure"
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
              onClick={()=> {}}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path fillRule="evenodd" d="M11.078 2.25c-.917 0-1.699.663-1.85 1.57l-.143.859a8.25 8.25 0 0 0-1.962 1.133l-.809-.334a1.875 1.875 0 0 0-2.401 1.09l-.75 1.846a1.875 1.875 0 0 0 1.09 2.401l.809.334a8.284 8.284 0 0 0 0 2.266l-.81.334a1.875 1.875 0 0 0-1.089 2.4l.75 1.847a1.875 1.875 0 0 0 2.4 1.089l.81-.334a8.25 8.25 0 0 0 1.961 1.133l.144.86c.15.906.932 1.569 1.85 1.569h1.5c.918 0 1.7-.663 1.85-1.57l.144-.859a8.25 8.25 0 0 0 1.961-1.133l.81.334a1.875 1.875 0 0 0 2.4-1.09l.75-1.846a1.875 1.875 0 0 0-1.089-2.401l-.81-.334a8.284 8.284 0 0 0 0-2.266l.81-.334a1.875 1.875 0 0 0 1.089-2.4l-.75-1.847a1.875 1.875 0 0 0-2.4-1.089l-.81.334a8.25 8.25 0 0 0-1.961-1.133l-.144-.86a1.875 1.875 0 0 0-1.85-1.569h-1.5Zm.75 8.25a2.25 2.25 0 1 0 0 4.5 2.25 2.25 0 0 0 0-4.5Z" clipRule="evenodd" />
              </svg>
            </button>
            <div className="w-40 h-32 rounded overflow-hidden bg-gray-100 flex-shrink-0">
              <img
                src="/simulations/lead-effective-meetings.jpeg"
                alt="Leading an Effective Meeting"
                className="w-full h-full object-cover"
                onError={(e) => {
                  const el = e.currentTarget as HTMLImageElement;
                  el.style.display = 'none';
                }}
              />
            </div>
            <div className="flex-1 flex flex-col py-1 gap-1">
              <div className="text-base font-semibold text-gray-900">Leading an Effective Meeting</div>
              <div className="text-sm text-gray-600">
                You're a seasoned team lead. You've been doing daily standup meetings for a while now, but you've received feedback that they tend to derail. Start the meeting by crisply outlineing the goals, stay on point and minimize ambiguity
              </div>
            </div>
            <div className="flex flex-col items-end justify-end gap-2">
              <button className="bg-[#25394D] hover:brightness-110 text-white py-2 px-3 rounded-md text-sm">Lets do it!</button>
            </div>
          </div>

          {/* 4) Conference Small Talk */}
          <div className="bg-white rounded-lg shadow-sm border p-4 hover:shadow transition-shadow flex flex-row items-stretch gap-4 relative">
            <button
              aria-label="Configure"
              title="Configure"
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
              onClick={()=> {}}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path fillRule="evenodd" d="M11.078 2.25c-.917 0-1.699.663-1.85 1.57l-.143.859a8.25 8.25 0 0 0-1.962 1.133l-.809-.334a1.875 1.875 0 0 0-2.401 1.09l-.75 1.846a1.875 1.875 0 0 0 1.09 2.401l.809.334a8.284 8.284 0 0 0 0 2.266l-.81.334a1.875 1.875 0 0 0-1.089 2.4l.75 1.847a1.875 1.875 0 0 0 2.4 1.089l.81-.334a8.25 8.25 0 0 0 1.961 1.133l.144.86c.15.906.932 1.569 1.85 1.569h1.5c.918 0 1.7-.663 1.85-1.57l.144-.859a8.25 8.25 0 0 0 1.961-1.133l.81.334a1.875 1.875 0 0 0 2.4-1.09l.75-1.846a1.875 1.875 0 0 0-1.089-2.401l-.81-.334a8.284 8.284 0 0 0 0-2.266l.81-.334a1.875 1.875 0 0 0 1.089-2.4l-.75-1.847a1.875 1.875 0 0 0-2.4-1.089l-.81.334a8.25 8.25 0 0 0-1.961-1.133l-.144-.86a1.875 1.875 0 0 0-1.85-1.569h-1.5Zm.75 8.25a2.25 2.25 0 1 0 0 4.5 2.25 2.25 0 0 0 0-4.5Z" clipRule="evenodd" />
              </svg>
            </button>
            <div className="w-40 h-32 rounded overflow-hidden bg-gray-100 flex-shrink-0">
              <img
                src="/simulations/small-talk.png"
                alt="Conference Small Talk"
                className="w-full h-full object-cover"
                onError={(e) => {
                  const el = e.currentTarget as HTMLImageElement;
                  el.style.display = 'none';
                }}
              />
            </div>
            <div className="flex-1 flex flex-col py-1 gap-1">
              <div className="text-base font-semibold text-gray-900">Conference Small Talk</div>
              <div className="text-sm text-gray-600">
                You're at a conference and you've just arrived at the first social event. There's a couple of folks talking at the table next you. Break into the conversation and engage with them for a few minutes. Try to figure out why one of them is here, what they do for work and what they're hoping to get from the conferece.
              </div>
            </div>
            <div className="flex flex-col items-end justify-end gap-2">
              <button className="bg-[#25394D] hover:brightness-110 text-white py-2 px-3 rounded-md text-sm">Lets do it!</button>
            </div>
          </div>

          {/* 5) Contract Negotiation */}
          <div className="bg-white rounded-lg shadow-sm border p-4 hover:shadow transition-shadow flex flex-row items-stretch gap-4 relative">
            <button
              aria-label="Configure"
              title="Configure"
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
              onClick={()=> {}}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path fillRule="evenodd" d="M11.078 2.25c-.917 0-1.699.663-1.85 1.57l-.143.859a8.25 8.25 0 0 0-1.962 1.133l-.809-.334a1.875 1.875 0 0 0-2.401 1.09l-.75 1.846a1.875 1.875 0 0 0 1.09 2.401l.809.334a8.284 8.284 0 0 0 0 2.266l-.81.334a1.875 1.875 0 0 0-1.089 2.4l.75 1.847a1.875 1.875 0 0 0 2.4 1.089l.81-.334a8.25 8.25 0 0 0 1.961 1.133l.144.86c.15.906.932 1.569 1.85 1.569h1.5c.918 0 1.7-.663 1.85-1.57l.144-.859a8.25 8.25 0 0 0 1.961-1.133l.81.334a1.875 1.875 0 0 0 2.4-1.09l.75-1.846a1.875 1.875 0 0 0-1.089-2.401l-.81-.334a8.284 8.284 0 0 0 0-2.266l.81-.334a1.875 1.875 0 0 0 1.089-2.4l-.75-1.847a1.875 1.875 0 0 0-2.4-1.089l-.81.334a8.25 8.25 0 0 0-1.961-1.133l-.144-.86a1.875 1.875 0 0 0-1.85-1.569h-1.5Zm.75 8.25a2.25 2.25 0 1 0 0 4.5 2.25 2.25 0 0 0 0-4.5Z" clipRule="evenodd" />
              </svg>
            </button>
            <div className="w-40 h-32 rounded overflow-hidden bg-gray-100 flex-shrink-0">
              <img
                src="/simulations/negotiation.jpeg"
                alt="Contract Negotiation"
                className="w-full h-full object-cover"
                onError={(e) => {
                  const el = e.currentTarget as HTMLImageElement;
                  el.style.display = 'none';
                }}
              />
            </div>
            <div className="flex-1 flex flex-col py-1 gap-1">
              <div className="text-base font-semibold text-gray-900">Contract Negotiation</div>
              <div className="text-sm text-gray-600">
                You've been asked to participate as a subject matter expert in a price negotiation for an upcoming purchase your company is evaluating. Consider the offer and try to reduce it by 20%.
              </div>
            </div>
            <div className="flex flex-col items-end justify-end gap-2">
              <button className="bg-[#25394D] hover:brightness-110 text-white py-2 px-3 rounded-md text-sm">Lets do it!</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimulationsPage;


